import { useTranslation } from "@sagot/i18n";
import { useLayoutEffect, useRef, useState } from "react";
import type { HistoryApi } from "./builder/history-context";
import { coalesceKeyOf, describeEdit } from "./edit-label";
import type { Definition } from "./types";
import { useDefinitionHistory, type OnEdit } from "./use-definition-history";
import { useHistoryKeys } from "./use-history-keys";

interface Options {
  initial: () => Definition;
  /** The keys Ctrl / Cmd + Z (undo) and Ctrl / Cmd + Shift + Z or Ctrl + Y (redo) work: the builder is on screen and can be edited. */
  keys: boolean;
  /** Something changed that is not saved yet: an edit, an undo or a redo. */
  onEdited: () => void;
}

/**
 * The workflow being edited with its undo / redo history (WF-P5-14): `change` is what the builder calls for every edit,
 * `api` what the toolbar, the keys and the toasts with an Undo button use. History is kept across saves; `reset` starts it
 * again when the draft is replaced (rolled back, or translations drafted by the assistant).
 */
export function useEditorHistory({ initial, keys, onEdited }: Options) {
  const { t } = useTranslation();
  const history = useDefinitionHistory(initial);
  // What is current right now, for callbacks that outlive a render (a toast's Undo button): the definition of the edit made
  // last (available before the next render) and the labels of what could be undone and redone.
  const live = useRef({ definition: history.definition, undoLabel: history.undoLabel, redoLabel: history.redoLabel });
  useLayoutEffect(() => {
    live.current = { definition: history.definition, undoLabel: history.undoLabel, redoLabel: history.redoLabel };
  });
  const [epoch, setEpoch] = useState(0);
  const [announcement, setAnnouncement] = useState("");

  const change: OnEdit = (next, options) => {
    const before = live.current.definition;
    live.current = { ...live.current, definition: next };
    history.commit(next, { coalesceKey: coalesceKeyOf(before, next), ...options, label: options?.label ?? describeEdit(before, next, t) });
    onEdited();
  };
  const undo = () => {
    const action = live.current.undoLabel;
    if (action === null) return;
    setAnnouncement(t("engage.workflows.extras.history.undone", { action }));
    history.undo();
    setEpoch((current) => current + 1);
    onEdited();
  };
  const redo = () => {
    const action = live.current.redoLabel;
    if (action === null) return;
    setAnnouncement(t("engage.workflows.extras.history.redone", { action }));
    history.redo();
    setEpoch((current) => current + 1);
    onEdited();
  };
  useHistoryKeys({ enabled: keys, onUndo: undo, onRedo: redo });

  const api: HistoryApi = {
    undoLabel: history.undoLabel,
    redoLabel: history.redoLabel,
    undo,
    redo,
    latest: () => live.current.definition,
    undoIfCurrent: (definition) => { if (live.current.definition === definition) undo(); },
    epoch,
    announcement,
  };
  /** A new starting point: nothing before it can be undone. */
  const reset = (definition: Definition) => {
    live.current = { definition, undoLabel: null, redoLabel: null };
    history.reset(definition);
    setEpoch((current) => current + 1);
    setAnnouncement("");
  };
  return { definition: history.definition, change, api, reset, resetToLatest: () => { reset(live.current.definition); } };
}
